/**
 * LazyComponentLoader — fetches server-ui components on scroll visibility.
 *
 * Handles the `delivery: 'lazy'` case in ComponentSlot. Uses an
 * IntersectionObserver to detect when the component scrolls near the
 * viewport, then fires the RTK Query hook to fetch component data.
 * Once data arrives, renders through ComponentSlot for normal dispatch.
 *
 * Skeleton fallback chain (add skeletons via ServerUiDomainProvider,
 * NOT by hardcoding them here):
 *   1. skeletons.byTypeKey[component.type_key]
 *   2. skeletons.byPrimitive[component.primitive]
 *   3. DefaultLazyPlaceholder (built-in)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Skeleton } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

// Import from store barrel, not components directory, to avoid circular deps.
import {
  useGetServerUiComponentByEndpointQuery,
  useGetServerUiComponentQuery,
} from '../store';
import type { ServerUiComponent } from '../types';

import { ComponentSlot } from './ComponentSlot';
import { useSkeletons } from './ServerUiDomainProvider';

type LazyComponentLoaderProps = {
  component: ServerUiComponent;
};

/**
 * Classify a lazy endpoint. The generic container route is
 * /v2/provider/containers/{containerKey}/components/{typeKey}?entity_id=… —
 * those keep using the keyed query (so the cache tags the refresh + manual
 * invalidation machinery rely on stay intact). A bespoke feature endpoint
 * (e.g. /v2/provider/semantic/analytics/components/{slug}) won't match the
 * shape; it's fetched verbatim instead. Returns empty containerKey/typeKey
 * when the endpoint isn't the container shape.
 */
const parseEndpoint = (endpoint: string) => {
  const url = new URL(endpoint, 'http://placeholder');
  const match = url.pathname.match(/\/containers\/([^/]+)\/components\/([^/]+)/);
  return {
    containerKey: match?.[1] ?? '',
    typeKey: match?.[2] ?? '',
    entityId: url.searchParams.get('entity_id') ?? undefined,
  };
};

const DefaultLazyPlaceholder: React.FC = () => (
  <Skeleton variant="rounded" sx={{height: 80,  borderRadius: 1.5 }} />
);

export const LazyComponentLoader: React.FC<LazyComponentLoaderProps> = ({ component }) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const skeletons = useSkeletons();

  // Parse endpoint args — memoized since endpoint is stable per component.
  // Returns empty strings if endpoint is null (defensive, shouldn't happen).
  const parsedArgs = useMemo(
    () => (component.endpoint ? parseEndpoint(component.endpoint) : { containerKey: '', typeKey: '', entityId: undefined }),
    [component.endpoint],
  );

  // IntersectionObserver — fire once when component scrolls near viewport
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || hasBeenVisible) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px', threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasBeenVisible]);

  // Container-shape endpoints keep the keyed query (unchanged cache tags);
  // bespoke feature endpoints fetch the server-provided URL verbatim. Both hooks
  // are always called (rules-of-hooks); the inactive one is skipped via skipToken.
  // Assumes `isContainerShape` is stable for a given component instance — true
  // for static lazy descriptors (the shape comes from the immutable endpoint).
  const isContainerShape = Boolean(parsedArgs.containerKey && parsedArgs.typeKey);
  const keyedQuery = useGetServerUiComponentQuery(
    hasBeenVisible && component.endpoint && isContainerShape ? parsedArgs : skipToken,
  );
  const endpointQuery = useGetServerUiComponentByEndpointQuery(
    hasBeenVisible && component.endpoint && !isContainerShape ? component.endpoint : skipToken,
  );
  const { data, isLoading, error } = isContainerShape ? keyedQuery : endpointQuery;

  // Defensive: lazy components must have an endpoint
  if (!component.endpoint) {
    console.warn(
      `[server-ui] LazyComponentLoader received component "${component.type_key}" with no endpoint`,
    );
    return null;
  }

  // Resolve skeleton via fallback chain
  const SkeletonComponent =
    skeletons?.byTypeKey?.[component.type_key as keyof typeof skeletons.byTypeKey] ??
    skeletons?.byPrimitive?.[component.primitive];

  const renderPlaceholder = () => {
    if (SkeletonComponent) {
      return <SkeletonComponent component={component} />;
    }
    return <DefaultLazyPlaceholder />;
  };

  // Before visible or loading — show skeleton
  if (!hasBeenVisible || isLoading) {
    return <div ref={sentinelRef}>{renderPlaceholder()}</div>;
  }

  // Error
  if (error) {
    return (
      <Alert severity="error" sx={{ my: 1 }}>
        Failed to load {component.type_key}
      </Alert>
    );
  }

  // Success — hand off to ComponentSlot for normal dispatch
  if (data) {
    return <ComponentSlot component={data} />;
  }

  return null;
};
